import { api } from '../auth/api';

// Blog post shapes mirror backend/blogs.py. List rows omit `body_markdown` to
// keep listings light; the detail fetch returns the full post.
export interface BlogListRow {
  slug: string;
  title: string;
  summary: string;
  cover_image_url: string;
  status: 'draft' | 'published';
  author_handle: string;
  tags: string[];
  /** Rough read-time estimate in whole minutes, precomputed server-side. */
  read_minutes: number;
  created_at: string;
  updated_at: string;
  published_at: string;
}

export interface BlogPost extends BlogListRow {
  body_markdown: string;
}

export interface BlogDraft {
  title: string;
  summary?: string;
  body_markdown?: string;
  cover_image_url?: string;
  tags?: string[];
  publish?: boolean;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error((detail as { detail?: string }).detail || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

// ── Public reads ────────────────────────────────────────────────────────────

export async function listPublishedBlogs(): Promise<BlogListRow[]> {
  const res = await api('/api/blogs');
  const data = await json<{ blogs: BlogListRow[] }>(res);
  return data.blogs;
}

export async function getPublishedBlog(slug: string): Promise<BlogPost> {
  return json<BlogPost>(await api(`/api/blogs/${encodeURIComponent(slug)}`));
}

// ── Admin reads/writes (token required) ─────────────────────────────────────

export async function listAllBlogs(token?: string): Promise<BlogListRow[]> {
  const data = await json<{ blogs: BlogListRow[] }>(await api('/api/admin/blogs', { token }));
  return data.blogs;
}

/** Full post incl. drafts — for the editor's load-for-edit. */
export async function getBlogForEdit(slug: string, token?: string): Promise<BlogPost> {
  return json<BlogPost>(await api(`/api/admin/blogs/${encodeURIComponent(slug)}`, { token }));
}

export async function createBlog(draft: BlogDraft, token?: string): Promise<BlogPost> {
  return json<BlogPost>(await api('/api/blogs', { method: 'POST', token, body: draft }));
}

export async function updateBlog(slug: string, patch: BlogDraft, token?: string): Promise<BlogPost> {
  return json<BlogPost>(await api(`/api/blogs/${encodeURIComponent(slug)}`, { method: 'PUT', token, body: patch }));
}

export async function deleteBlog(slug: string, token?: string): Promise<void> {
  const res = await api(`/api/blogs/${encodeURIComponent(slug)}`, { method: 'DELETE', token });
  if (!res.ok && res.status !== 404) throw new Error(`Delete failed (${res.status})`);
}
